import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { Save, Camera, Video, Coins } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CreditSettingsTab = () => {
  const [settings, setSettings] = useState({
    photo_download_credits: 10,
    video_download_credits: 20,
    monthly_subscription_credits: 20,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/admin/credit-settings`);
        setSettings(res.data);
      } catch { /* use defaults */ }
      setLoading(false);
    };
    fetch();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API_URL}/api/admin/credit-settings`, settings);
      setSettings(res.data);
      toast.success('Credit settings saved');
    } catch {
      toast.error('Failed to save settings');
    }
    setSaving(false);
  };

  if (loading) return <div className="flex justify-center py-12"><div className="spinner" /></div>;

  return (
    <div className="space-y-6" data-testid="credit-settings-tab">
      <p className="text-sm text-muted-foreground">
        Configure how many credits each download costs. Coaches and admins are always exempt. Subscribers receive monthly credits that reset each billing cycle.
      </p>

      <div className="grid gap-6 sm:grid-cols-3">
        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2 text-primary">
              <Camera className="w-5 h-5" />
              <Label className="font-semibold text-sm uppercase">Photo Download</Label>
            </div>
            <Input
              type="number"
              min={0}
              value={settings.photo_download_credits}
              onChange={e => setSettings(s => ({ ...s, photo_download_credits: parseInt(e.target.value) || 0 }))}
              data-testid="photo-credits-input"
            />
            <p className="text-xs text-muted-foreground">Credits per photo download</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2 text-primary">
              <Video className="w-5 h-5" />
              <Label className="font-semibold text-sm uppercase">Video Download</Label>
            </div>
            <Input
              type="number"
              min={0}
              value={settings.video_download_credits}
              onChange={e => setSettings(s => ({ ...s, video_download_credits: parseInt(e.target.value) || 0 }))}
              data-testid="video-credits-input"
            />
            <p className="text-xs text-muted-foreground">Credits per video download</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2 text-primary">
              <Coins className="w-5 h-5" />
              <Label className="font-semibold text-sm uppercase">Monthly Credits</Label>
            </div>
            <Input
              type="number"
              min={0}
              value={settings.monthly_subscription_credits}
              onChange={e => setSettings(s => ({ ...s, monthly_subscription_credits: parseInt(e.target.value) || 0 }))}
              data-testid="monthly-credits-input"
            />
            <p className="text-xs text-muted-foreground">Credits granted to subscribers each month (resets monthly)</p>
          </CardContent>
        </Card>
      </div>

      <Button onClick={handleSave} disabled={saving} className="rounded-full" data-testid="save-credit-settings-btn">
        <Save className="w-4 h-4 mr-2" />
        {saving ? 'Saving...' : 'Save Settings'}
      </Button>
    </div>
  );
};

export default CreditSettingsTab;
